// TEAAM EMS TypeScript Definitions

export enum UserRole {
  ADMIN = 'ADMIN',
  SUPERVISOR = 'SUPERVISOR',
  DISPATCHER = 'DISPATCHER',
  RESPONDER = 'RESPONDER',
}

export enum MissionType {
  SCENE_CALL = 'SCENE_CALL',
  INTER_FACILITY = 'INTER_FACILITY',
  STANDBY = 'STANDBY',
  TRAINING = 'TRAINING',
  REPATRIATION = 'REPATRIATION',
  TELEHEALTH = 'TELEHEALTH',
}

export enum Priority {
  CRITICAL = 'CRITICAL',
  URGENT = 'URGENT',
  ROUTINE = 'ROUTINE',
}

export enum MissionStatus {
  RECEIVED = 'RECEIVED',
  ASSIGNED = 'ASSIGNED',
  DISPATCHED = 'DISPATCHED',
  CREW_NOTIFIED = 'CREW_NOTIFIED',
  AIRCRAFT_NOTIFIED = 'AIRCRAFT_NOTIFIED',
  AIRCRAFT_LAUNCHED = 'AIRCRAFT_LAUNCHED',
  AIRCRAFT_AT_CREW_BASE = 'AIRCRAFT_AT_CREW_BASE',
  CREW_ABOARD = 'CREW_ABOARD',
  ENROUTE_TO_SCENE = 'ENROUTE_TO_SCENE',
  ON_SCENE = 'ON_SCENE',
  PATIENT_CONTACT = 'PATIENT_CONTACT',
  TRANSPORTING = 'TRANSPORTING',
  AT_DESTINATION = 'AT_DESTINATION',
  MISSION_COMPLETE = 'MISSION_COMPLETE',
  CANCELLED = 'CANCELLED',
  STANDBY = 'STANDBY',
  CLOSED = 'CLOSED',
}

export interface User {
  id: string;
  username: string;
  role: string;  // Changed from UserRole to string to match backend response
  fullName: string;
  email: string;
  phone?: string;
  preferredInitials?: string;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Base {
  id: string;
  name: string;
  code: string;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface AircraftContractor {
  id: string;
  companyName: string;
  contactName?: string;
  contactPhone?: string;
  dispatchPhone?: string;
  aircraftType?: string;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Mission {
  id: string;
  missionNumber: string;
  isTestTraining: boolean;
  missionType: MissionType;
  priority: Priority;
  status: MissionStatus;
  
  // Base Information
  primaryBaseId?: string;
  primaryBase?: Base;
  
  // Location Information
  sceneLocation?: string;
  pickupLocation?: string;
  destinationLocation?: string;
  sceneGpsLat?: number;
  sceneGpsLong?: number;
  destinationGpsLat?: number;
  destinationGpsLong?: number;
  what3words?: string;
  
  // Landing Zone Assessment
  lzGpsLat?: number;
  lzGpsLong?: number;
  lzSurfaceType?: string;
  lzObstaclesWires?: string;
  lzNotes?: string;
  
  // Caller Information
  callerAgency?: string;
  callerName?: string;
  callerPhone?: string;
  callerIncidentNumber?: string;
  
  // Patient Information
  patientCount: number;
  
  // EMS Timestamps
  timeReceived?: Date;
  timeAssigned?: Date;
  timeAircraftNotified?: Date;
  timeAircraftLaunched?: Date;
  timeAircraftAtCrewBase?: Date;
  timeCrewAboard?: Date;
  timeEnrouteToScene?: Date;
  timeOnScene?: Date;
  timePatientContact?: Date;
  timeTransporting?: Date;
  timeAtDestination?: Date;
  timeMissionComplete?: Date;
  
  // Time Display
  timeLocalString?: string;
  timeZuluString?: string;
  
  // General Notes
  incidentNotes?: string;
  patientNotes?: string;
  resourceNotes?: string;
  communicationsNotes?: string;
  weatherNotes?: string;
  
  // Special Equipment
  selectedEquipment?: string[];

  // Ground Resources
  groundResources?: string[];
  
  // Medical Documentation
  verbalOrdersDoc?: string;
  
  // Weather Information
  weatherConditions?: string;
  weatherImpact?: string;
  
  // System Fields
  versionHash?: string;
  dateCreated: Date;
  dateUpdated: Date;
  dateClosed?: Date | null;
  createdBy: string;
  
  // Relations
  creator?: User;
  patients?: MissionPatient[];
  aircraft?: MissionAircraft[];
  comments?: MissionComment[];
  radioFrequencies?: MissionRadioFrequency[];
}

export interface MissionPatient {
  id: string;
  missionId: string;
  patientNumber: number;
  age?: number;
  gender?: string;
  weight?: number;
  chiefComplaint?: string;
  triagePriority?: Priority;
  vitalSigns?: string;
  treatmentNotes?: string;
}

export interface MissionAircraft {
  id: string;
  missionId: string;
  contractorId: string;
  tailNumber?: string;
  aircraftType?: string;
  assignedAt: Date;
  notes?: string;
  contractor?: AircraftContractor;
}

export interface MissionComment {
  id: string;
  missionId: string;
  dispatcherId: string;
  dispatcherInitials: string;
  commentText: string;
  isSystemEvent?: boolean;
  timestamp: Date;
  dispatcher?: User;
}

export interface MissionRadioFrequency {
  id: string;
  missionId: string;
  name: string;
  frequency: string;
  type: 'PRIMARY' | 'HOSPITAL' | 'CUSTOM';
  sortOrder: number;
  createdAt: Date;
}

export interface AuditLog {
  id: string;
  tableName: string;
  recordId: string;
  actionType: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  changedBy: string;
  changedAt: Date;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
  immutableHash: string;
}

// API Request/Response Types
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
  expiresIn: number;
}

export interface CreateMissionRequest {
  missionType: MissionType;
  priority: Priority;
  primaryBaseId: string;
  sceneLocation?: string;
  callerAgency?: string;
  callerName?: string;
  callerPhone?: string;
}

export interface UpdateMissionRequest {
  primaryBaseId?: string;
  [key: string]: any;
}

export interface MissionListResponse {
  missions: Mission[];
  total: number;
  page: number;
  limit: number;
}

export interface MissionFilters {
  status?: MissionStatus[];
  priority?: Priority[];
  missionType?: MissionType[];
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
  includeTestTraining?: boolean;
}

// UI Component Types
export interface TabItem {
  id: string;
  label: string;
  icon?: string;
  component: React.ComponentType<any>;
}

export interface NotificationToast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  actions?: Array<{
    label: string;
    onClick: () => void;
  }>;
}

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  type: 'scene' | 'hospital' | 'aircraft' | 'lz';
  title: string;
  description?: string;
  priority?: Priority;
  status?: MissionStatus;
}

// WebSocket Event Types
export interface WebSocketMessage {
  type: string;
  data: any;
  timestamp: Date;
}

export interface MissionUpdateEvent {
  missionId: string;
  changes: Partial<Mission>;
  updatedBy: string;
  timestamp: Date;
}

export interface NewCommentEvent {
  missionId: string;
  comment: MissionComment;
  timestamp: Date;
}

// Form Types
export interface MissionFormData {
  missionType: MissionType;
  priority: Priority;
  sceneLocation: string;
  destinationLocation: string;
  callerAgency: string;
  callerName: string;
  callerPhone: string;
  patientCount: number;
  // ... other form fields
}

// Utility Types
export type MissionTimestamp = keyof Pick<Mission, 
  'timeReceived' | 'timeAssigned' | 'timeAircraftNotified' | 
  'timeAircraftLaunched' | 'timeAircraftAtCrewBase' | 'timeCrewAboard' |
  'timeEnrouteToScene' | 'timeOnScene' | 'timePatientContact' |
  'timeTransporting' | 'timeAtDestination' | 'timeMissionComplete'
>;

export type MissionNotes = keyof Pick<Mission,
  'incidentNotes' | 'patientNotes' | 'resourceNotes' | 
  'communicationsNotes' | 'weatherNotes'
>;

// Configuration Types
export interface AppConfig {
  apiUrl: string;
  socketUrl: string;
  mapConfig: {
    defaultCenter: { lat: number; lng: number };
    defaultZoom: number;
    maxZoom: number;
    minZoom: number;
  };
  features: {
    enableWhat3Words: boolean;
    enableWeatherIntegration: boolean;
    enableOfflineMode: boolean;
  };
}

// Store Types (for Zustand)
export interface AuthStore {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

export interface MissionStore {
  missions: Mission[];
  currentMission: Mission | null;
  loading: boolean;
  error: string | null;
  filters: MissionFilters;
  fetchMissions: (filters?: MissionFilters) => Promise<void>;
  createMission: (data: CreateMissionRequest) => Promise<Mission>;
  updateMission: (id: string, data: UpdateMissionRequest) => Promise<Mission>;
  setCurrentMission: (mission: Mission | null) => void;
  setFilters: (filters: MissionFilters) => void;
}

export interface UIStore {
  notifications: NotificationToast[];
  sidebarOpen: boolean;
  darkMode: boolean;
  addNotification: (notification: Omit<NotificationToast, 'id'>) => void;
  removeNotification: (id: string) => void;
  toggleSidebar: () => void;
  toggleDarkMode: () => void;
}

// Base API Types
export interface CreateBaseRequest {
  name: string;
  code: string;
  active?: boolean;
}

export interface UpdateBaseRequest {
  name?: string;
  code?: string;
  active?: boolean;
}

export interface BaseListResponse {
  bases: Base[];
  total: number;
}

// Contractor API Types
export interface CreateContractorRequest {
  companyName: string;
  contactName?: string;
  contactPhone?: string;
  dispatchPhone?: string;
  aircraftType?: string;
  active?: boolean;
}

export interface UpdateContractorRequest {
  companyName?: string;
  contactName?: string;
  contactPhone?: string;
  dispatchPhone?: string;
  aircraftType?: string;
  active?: boolean;
}

export interface ContractorListResponse {
  contractors: AircraftContractor[];
  total: number;
}

// Response wrapper for API
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  errors?: string[];
}

// Pagination
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}